import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { Button, TermsCheckbox } from 'cvlib';
import Metrics from 'services/metrics';
import TermsAndConditions from './modal/TermsAndConditions';
import { getTranslation } from '../../utils/getTranslation';
import { acceptTerms } from './actions/termsAndConditions.actions';
import { useNavigate } from 'react-router-dom';
import {
  REGION,
  APA_KEYS,
  ASSET_KEY,
  PRIVACY_POLICY,
  TERMS_AND_CONDITIONS,
  CONTINUE,
  DISCLAIMER_MAP,
  METRICS_EVENT,
  METRICS_METHOD,
  METRICS_SELECTION,
  DISCLAIMER_REGEXP,
  HREF_DISCLAIMER_MAP,
} from './constants';

import './PrivacyPolicy.scss';
import getFirstNode from 'utils/getFirstNode';

const handleBg = isBgActive => (!isBgActive ? 'privacy-policy--gradient' : '');

const PrivacyPolicy = ({ isBackgroundActive }) => {
  const dispatch = useDispatch();
  const translations = useSelector(state => state.translations);
  const user = useSelector(state => state.user);
  const assets = useSelector(state => state.asset);
  const navigate = useNavigate();
  
  const [acceptPrivacyPolicy, setAcceptPrivacyPolicy] = useState(false);
  const [isSubmitDisabled, setSubmitDisabled] = useState(true);
  const [isDisclaimerVisible, setDisclaimer] = useState(false);
  const [isTooltipVisible, setTooltip] = useState(false);
  const [activeApaKey, setActiveApaKey] = useState();
  const [modalRoutes, setModalRoutes] = useState([]);

  const firstNode = getFirstNode();

  const getMetricsBody = selectionKey => ({
    event: METRICS_EVENT,
    selection: METRICS_SELECTION.get(selectionKey),
    method: METRICS_METHOD,
    user_id: user?.user_id || '',
    user_type: user?.userType === 'ANONYMUS' ? 'anonimo' : 'registrado',
    country: REGION,
  });

  const handleCheckbox = () => {
    setAcceptPrivacyPolicy(!acceptPrivacyPolicy);
    setSubmitDisabled(!isSubmitDisabled);
  };

  const handleDisclaimer = disclaimerKey => {
    const metricsBody = getMetricsBody(disclaimerKey);

    Metrics.newEvent(metricsBody);
    // youboraM.fireEvent('Aviso de privacidad', metricsBody);

    const route = DISCLAIMER_MAP.get(disclaimerKey);
    setModalRoutes([...modalRoutes, route]);
    setDisclaimer(true);
  };

  const handleSubmit = async submitKey => {
    if (!acceptPrivacyPolicy) {
      setTooltip(true);
      return;
    }

    setSubmitDisabled(true);

    const metricsBody = getMetricsBody(submitKey);

    Metrics.newEvent(metricsBody);
    // youboraM.fireEvent('Aviso de privacidad', metricsBody);

    try {
      const acceptedTerms = await dispatch(acceptTerms());
      if (acceptedTerms) navigate(`/${REGION}/${firstNode}`);
    } catch {
      setSubmitDisabled(false);
    }
  };

  const getTranslationByKey = key => getTranslation(key, translations);
  const getAssetByKey = key => assets[key];

  const updateApaKeyOnAnchorClick = href => {
    const [disclaimerKey] = href.match(DISCLAIMER_REGEXP) || [];

    if (!disclaimerKey) {
      window.location.href = href;
      return;
    }

    const currentRoute = [...modalRoutes].pop();

    if (currentRoute === disclaimerKey) return;
    setModalRoutes([...modalRoutes, disclaimerKey]);
  };

  const handleModalReturn = () => setModalRoutes([...modalRoutes].slice(0, -1));

  useEffect(
    () => {
      if (modalRoutes.length === 0) {
        setDisclaimer(false);
        return;
      }

      const modalRoute = [...modalRoutes].pop();
      const apaFullText = HREF_DISCLAIMER_MAP.get(modalRoute);
      setActiveApaKey(apaFullText);
    },
    [modalRoutes],
  );

  return (
    <>
      {isBackgroundActive && (
        <div
          className="privacy-policy-background"
          style={{
            backgroundImage: `url(${getAssetByKey(ASSET_KEY.BACKGROUND)})`,
          }}
        />
      )}

      {isDisclaimerVisible && (
        <TermsAndConditions
          onAnchorClick={updateApaKeyOnAnchorClick}
          onReturnClick={handleModalReturn}
          apaKey={activeApaKey}
        />
      )}

      <div className={`privacy-policy ${handleBg(isBackgroundActive)}`}>
        <header className="privacy-policy__header">
          <img src={getAssetByKey(ASSET_KEY.CLARO_LOGO)} alt="logo" />
        </header>

        <section className="privacy-policy__section">
          <div className="privacy-policy__section__form">
            <div className="privacy-policy__section__form__title">
              <span>{getTranslationByKey(APA_KEYS.TITLE)}</span>
            </div>

            <div className="privacy-policy__section__form__wrapper">
              <div className="privacy-policy__section__form__informative-text">
                <span>{getTranslationByKey(APA_KEYS.SUBTITLE)}</span>
              </div>

              <div className="privacy-policy__section__form__checkbox">
                <TermsCheckbox
                  message={getTranslationByKey(APA_KEYS.CHECKBOX_TOOLTIP)}
                  onChange={handleCheckbox}
                  clearErrors={() => setTooltip(false)}
                  error={isTooltipVisible}
                  clearErrorsOnClick
                >
                  {getTranslationByKey(APA_KEYS.CHECKBOX_LABEL)}
                </TermsCheckbox>
              </div>
            </div>

            <div className="privacy-policy__section__form__buttons">
              <Button
                onClick={() => handleDisclaimer(TERMS_AND_CONDITIONS)}
                type="gray"
              >
                {getTranslationByKey(APA_KEYS.TERMS_AND_CONDITIONS)}
              </Button>

              <Button
                onClick={() => handleDisclaimer(PRIVACY_POLICY)}
                type="gray"
              >
                {getTranslationByKey(APA_KEYS.PRIVACY_POLICY)}
              </Button>

              <Button
                className={isSubmitDisabled ? 'button-disabled' : ''}
                onClick={() => handleSubmit(CONTINUE)}
                type="red"
              >
                {getTranslationByKey(APA_KEYS.CONTINUE)}
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

PrivacyPolicy.propTypes = {
  asset: PropTypes.object,
  translations: PropTypes.object,
  isBackgroundActive: PropTypes.bool,
};

export default PrivacyPolicy;
